/**
 * Bucket-by-condition editor (autocoding). For a SELECT target, each answer
 * option gets a boolean condition built with the Logic Wizard's ConditionGroup.
 * Produces one `target::<<option>> = <premise>` assignment per bucket.
 */
import React from "react"
import { Button, Select, Tooltip, Empty, Input } from "antd"
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons"
import { LabelItem } from "utils/labelUtils"
import { ASTNode, GroupNode, PlaceholderNode } from "utils/logicWizard/grammar"
import {
  QuestionInfo,
  resolveAnswerOptions,
} from "utils/logicWizard/questionTypeDetector"
import { BucketAssignment } from "utils/recodeWizard/recodeModel"
import { ConditionGroup } from "../LogicWizard"

interface BucketByConditionEditorProps {
  /** Info for the recode target — used to resolve its answer options. */
  targetInfo: QuestionInfo | undefined
  buckets: BucketAssignment[]
  /** Labels/info/listMap for the CONDITION builder (all source questions). */
  labels: LabelItem[]
  questionInfo: Map<string, QuestionInfo>
  listMap: ReadonlyMap<string, LabelItem>
  onChange: (buckets: BucketAssignment[]) => void
}

const placeholder: PlaceholderNode = { kind: "placeholder" }

function makeRootGroup(): GroupNode {
  return { kind: "group", op: "AND", children: [placeholder] }
}

function asGroup(ast: ASTNode): GroupNode {
  return ast.kind === "group" ? ast : { kind: "group", op: "AND", children: [ast] }
}

const BucketByConditionEditor: React.FC<BucketByConditionEditorProps> = ({
  targetInfo,
  buckets,
  labels,
  questionInfo,
  listMap,
  onChange,
}) => {
  const answerOptions = targetInfo
    ? resolveAnswerOptions(targetInfo, listMap)
    : []
  const usedOptions = new Set(buckets.map((b) => b.option).filter(Boolean))

  const updateBucket = (idx: number, next: Partial<BucketAssignment>) => {
    onChange(buckets.map((b, i) => (i === idx ? { ...b, ...next } : b)))
  }

  const removeBucket = (idx: number) => {
    onChange(buckets.filter((_, i) => i !== idx))
  }

  const addBucket = () => {
    onChange([...buckets, { option: "", ast: makeRootGroup() }])
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs text-gray-500">
        Set each answer option of the target based on a condition. Options with
        no condition are left for the respondent.
      </div>

      {buckets.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No buckets yet"
        />
      )}

      {buckets.map((bucket, idx) => (
        <div key={idx} className="border rounded-lg p-3 bg-white">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium">Set option</span>
            {answerOptions.length > 0 ? (
              <Select
                showSearch
                size="small"
                style={{ minWidth: 220 }}
                placeholder="Choose an answer option…"
                value={bucket.option || undefined}
                onChange={(v) => updateBucket(idx, { option: v })}
                options={answerOptions.map((o) => ({
                  value: o,
                  label: o,
                  disabled: o !== bucket.option && usedOptions.has(o),
                }))}
              />
            ) : (
              <Input
                size="small"
                style={{ minWidth: 220 }}
                placeholder="Answer option text…"
                value={bucket.option}
                onChange={(e) => updateBucket(idx, { option: e.target.value })}
              />
            )}
            <span className="text-sm font-medium">when…</span>
            <Tooltip title="Remove this bucket">
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                className="ml-auto"
                onClick={() => removeBucket(idx)}
              />
            </Tooltip>
          </div>
          <ConditionGroup
            node={asGroup(bucket.ast)}
            labels={labels}
            questionInfo={questionInfo}
            listMap={listMap}
            onChange={(n) => updateBucket(idx, { ast: n })}
          />
        </div>
      ))}

      <div>
        <Button type="dashed" icon={<PlusOutlined />} onClick={addBucket}>
          Add bucket
        </Button>
      </div>
    </div>
  )
}

export default BucketByConditionEditor
